import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminSetup2FA = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('loading'); // loading, setup, verify
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_access_token');
    if (!token) {
      navigate('/admin/login', { replace: true });
      return;
    }
    initSetup(token);
  }, [navigate]);

  const initSetup = async (token) => {
    try {
      const res = await axios.post(`${API_URL}/api/admin/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setStep('setup');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login', { replace: true });
      } else {
        setError('Failed to initialize 2FA setup. Please try again.');
        setStep('setup');
      }
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const token = localStorage.getItem('admin_access_token');
    try {
      await axios.post(`${API_URL}/api/admin/auth/2fa/verify`, 
        { totp_code: totpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update stored user data
      const storedUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
      storedUser.totp_enabled = true;
      localStorage.setItem('admin_user', JSON.stringify(storedUser));

      setStep('success');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/admin/dashboard', { replace: true });
  };

  if (step === 'loading') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center', color: '#a1a1aa' }}>Setting up two-factor authentication...</div>
        </div>
      </div>
    );
  }

  if (step === 'success') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#x2705;</div>
            <h1 style={styles.heading}>2FA Activated</h1>
            <p style={styles.subtext}>
              Your account is now secured with two-factor authentication. You will need your authenticator app every time you log in.
            </p>
            <button
              onClick={handleContinue}
              data-testid="2fa-continue-btn"
              style={styles.primaryButton}
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.heading}>Set Up Two-Factor Authentication</h1>
        <p style={styles.subtext}>
          All admin accounts require 2FA for security. Scan the QR code below with an authenticator app (Google Authenticator, Authy, etc.)
        </p>

        {error && (
          <div style={styles.errorBox} data-testid="2fa-error">
            {error}
          </div>
        )}

        {qrCode && (
          <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
            <div style={styles.qrContainer}>
              <img 
                src={qrCode} 
                alt="2FA QR Code" 
                style={{ width: '200px', height: '200px' }}
                data-testid="2fa-qr-code"
              />
            </div>

            <div style={styles.secretBox}>
              <p style={{ margin: '0 0 0.25rem', fontSize: '0.75rem', color: '#a1a1aa' }}>
                Manual entry key:
              </p>
              <code 
                style={{ fontSize: '0.85rem', color: '#F2D857', letterSpacing: '2px', wordBreak: 'break-all' }}
                data-testid="2fa-secret"
              >
                {secret}
              </code>
            </div>
          </div>
        )}

        <form onSubmit={handleVerify}>
          <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem', color: '#e4e4e7' }}>
            Enter 6-digit code from your app
          </label>
          <input
            type="text"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            autoComplete="one-time-code"
            autoFocus
            required
            maxLength={6}
            data-testid="2fa-code-input"
            style={styles.codeInput}
          />
          <button
            type="submit"
            disabled={loading || totpCode.length !== 6}
            data-testid="2fa-verify-btn"
            style={{
              ...styles.primaryButton,
              opacity: (loading || totpCode.length !== 6) ? 0.5 : 1,
              cursor: (loading || totpCode.length !== 6) ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Verifying...' : 'Verify & Activate 2FA'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0a0a0a',
    padding: '1rem'
  },
  card: {
    background: '#111',
    padding: '2.5rem',
    borderRadius: '1rem',
    border: '1px solid #262626',
    width: '100%',
    maxWidth: '480px'
  },
  heading: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#F2D857',
    marginBottom: '0.5rem',
    textAlign: 'center'
  },
  subtext: {
    color: '#a1a1aa',
    fontSize: '0.9rem',
    lineHeight: '1.6',
    textAlign: 'center',
    marginBottom: '1.5rem'
  },
  errorBox: {
    padding: '0.75rem 1rem',
    marginBottom: '1rem',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '0.5rem',
    color: '#ef4444',
    fontSize: '0.875rem'
  },
  qrContainer: {
    display: 'inline-block',
    padding: '1rem',
    background: 'white',
    borderRadius: '0.75rem'
  },
  secretBox: {
    marginTop: '1rem',
    padding: '0.75rem',
    background: '#1a1a1a',
    borderRadius: '0.5rem',
    border: '1px solid #262626'
  },
  codeInput: {
    width: '100%',
    padding: '1rem',
    border: '2px solid #F2D857',
    borderRadius: '0.5rem',
    fontSize: '1.5rem',
    textAlign: 'center',
    letterSpacing: '0.5rem',
    fontWeight: 'bold',
    background: '#1a1a1a',
    color: '#fff',
    marginBottom: '1rem',
    outline: 'none',
    boxSizing: 'border-box'
  },
  primaryButton: {
    width: '100%',
    padding: '0.875rem',
    background: '#F2D857',
    color: '#000',
    border: 'none',
    borderRadius: '0.5rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem'
  }
};

export default AdminSetup2FA;
